import type { JSX } from 'react';
import {
  Node,
  mergeAttributes,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type NodeViewProps,
} from '@tiptap/react';
import { useEffect, useState } from 'react';
import { DeleteOutlined, LoadingOutlined, PlayCircleOutlined } from '@ant-design/icons';

import { api } from '../lib/api';

/**
 * FkVideo — видео с нашего S3 (фаза 0.2 «Красивые статьи»).
 *
 * Атрибуты: `id` — вложение (wiki_attachments, kind=VIDEO), `caption` —
 * подпись. В .md сериализуется fenced-блоком
 *   ```video
 *   {"id":"<attachmentId>","caption":"Как принимать поставку"}
 *   ```
 * HTML (редактор и читалка одинаково, см. markdown-html.ts / бэк
 * wiki-markdown.service.ts):
 *   <figure class="fk-video" data-video="<id>" data-caption="…">
 *     <video controls preload="metadata" playsinline poster=…/poster src=…/stream></video>
 *     <figcaption>…</figcaption>
 *   </figure>
 * В редакторе показываем постер и поле подписи; пока бэк транскодирует —
 * «Обрабатывается» с опросом /video-meta раз в 10 с.
 */
export interface FkVideoMeta {
  status: 'uploaded' | 'processing' | 'ready' | 'error' | null;
  error: string | null;
  durationSec: number | null;
  hasPoster: boolean;
}

export const videoStreamUrl = (id: string): string => `/api/wiki/attachments/${id}/stream`;
export const videoPosterUrl = (id: string): string => `/api/wiki/attachments/${id}/poster`;

export const FkVideo = Node.create({
  name: 'fkVideo',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('data-video') || '',
        renderHTML: () => ({}),
      },
      caption: {
        default: '',
        parseHTML: (el: HTMLElement) =>
          el.getAttribute('data-caption') || el.querySelector('figcaption')?.textContent || '',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'figure[data-video]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const id = String(node.attrs.id || '');
    const caption = String(node.attrs.caption || '');
    return [
      'figure',
      mergeAttributes(HTMLAttributes, { class: 'fk-video', 'data-video': id, 'data-caption': caption }),
      ['video', { controls: 'true', preload: 'metadata', playsinline: 'true', poster: videoPosterUrl(id), src: videoStreamUrl(id) }],
      ...(caption ? [['figcaption', {}, caption]] : []),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FkVideoView);
  },
});

/** Опрос статуса обработки; останавливается, когда видео готово или упало. */
export function useVideoMeta(id: string): FkVideoMeta | null {
  const [meta, setMeta] = useState<FkVideoMeta | null>(null);
  useEffect(() => {
    if (!id) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = async (): Promise<void> => {
      try {
        const m = await api.get<FkVideoMeta>(`/wiki/attachments/${id}/video-meta`);
        if (stop) return;
        setMeta(m);
        if (m.status === 'ready' || m.status === 'error') return;
      } catch {
        if (stop) return;
        setMeta({ status: 'error', error: 'Видео не найдено', durationSec: null, hasPoster: false });
        return;
      }
      timer = setTimeout(() => void load(), 10_000);
    };
    void load();
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);
  return meta;
}

export function formatDuration(sec: number | null): string {
  if (sec == null) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function FkVideoView(props: NodeViewProps): JSX.Element {
  const { node, updateAttributes, deleteNode, selected } = props;
  const id = String(node.attrs.id || '');
  const caption = String(node.attrs.caption || '');
  const meta = useVideoMeta(id);
  const ready = meta?.status === 'ready';

  return (
    <NodeViewWrapper className="fk-video-node" data-selected={selected}>
      <figure className="fk-video" contentEditable={false}>
        {ready ? (
          <video controls preload="metadata" playsInline poster={meta?.hasPoster ? videoPosterUrl(id) : undefined} src={videoStreamUrl(id)} />
        ) : (
          <div className="fk-video-processing">
            {meta?.status === 'error' ? (
              <>
                <PlayCircleOutlined style={{ fontSize: 28 }} />
                <div>Видео не обработано: {meta.error ?? 'ошибка'}</div>
              </>
            ) : (
              <>
                <LoadingOutlined spin style={{ fontSize: 28 }} />
                <div>Видео обрабатывается — обычно 1–3 минуты. Можно продолжать писать.</div>
              </>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
          <input
            value={caption}
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            placeholder="Подпись к видео"
            className="fk-video-caption-input"
          />
          {meta?.durationSec != null && (
            <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', flexShrink: 0 }}>{formatDuration(meta.durationSec)}</span>
          )}
          <button type="button" onClick={() => deleteNode()} title="Удалить видео" className="fk-video-del">
            <DeleteOutlined />
          </button>
        </div>
      </figure>
    </NodeViewWrapper>
  );
}
